import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown, ListIcon, Send, XIcon, InfoIcon, CheckSquareIcon, CheckIcon, AlertTriangleIcon, ArrowLeftIcon, XCircle, RefreshCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, useDrag } from '@use-gesture/react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { FeedbackPage } from './FeedbackPage';
export function AssessmentContent({
  onAssessmentStatusChange,
  onOpenSidebar
}) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [textAnswers, setTextAnswers] = useState({});
  const [confidenceLevel, setConfidenceLevel] = useState({});
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [isOnSubmissionScreen, setIsOnSubmissionScreen] = useState(false);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [incompleteQuestions, setIncompleteQuestions] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(true); // Changed to true to show results immediately
  const [retakingAssessment, setRetakingAssessment] = useState(false);
  const [incorrectQuestions, setIncorrectQuestions] = useState([]);
  const [isScenarioExpanded, setIsScenarioExpanded] = useState(false); // Start collapsed
  const [messages, setMessages] = useState([{
    type: 'bot',
    content: "Hi! I'm your AI assistant. How can I help you with this question?"
  }]);
  const [progressDotStartIndex, setProgressDotStartIndex] = useState(0);
  const [desktopDotStartIndex, setDesktopDotStartIndex] = useState(0);
  // Update parent component when assessment status changes
  useEffect(() => {
    if (onAssessmentStatusChange) {
      onAssessmentStatusChange(isSubmitted, isSubmitted);
    }
  }, [isSubmitted, onAssessmentStatusChange]);
  // Array of unique questions with different types - MUST be defined before getActiveQuestions
  const questions = [{
    type: 'multiple-choice',
    text: 'You are a new employee at a big skincare company on the product development team. You have done a great job in your first few months, and your manager is excited for you to join market research studies, visit stores, and have weekly meetings with important people. Before you meet customers and retailers, your manager wants you to attend the weekly meeting with the team. The manager believes this will help you understand what is happening, hear about opportunities and problems from the managers, and learn how they plan for the future. This will help you feel more confident when you go out into the field. You feel a little nervous as you enter the meeting because it is your first time meeting many leaders.',
    question: 'What is the main benefit of observing this stakeholder meeting before actively participating?',
    instruction: 'Check all that apply:',
    options: ['It can help you establish stronger trust with the stakeholders.', 'It allows you to gather information without needing to participate in discussions.', 'It can help the stakeholders see your dominant personality.', 'It helps you avoid making mistakes by staying silent in meetings.']
  }, {
    type: 'single-choice',
    text: 'A project stakeholder has requested a significant change to the project scope that would require additional resources and time.',
    question: 'What is your first step in handling this change request?',
    instruction: 'Select the most appropriate response:',
    options: ['Immediately agree to the change to maintain a good relationship.', 'Document the change request and analyze its impact on the project constraints.', "Reject the request since it wasn't in the original plan.", 'Implement the change without telling the rest of the team.']
  }, {
    type: 'text',
    text: 'In project management, conflicting priorities from different stakeholders are common challenges that require careful navigation.',
    question: 'Describe a situation where you had to manage conflicting priorities from different stakeholders. How did you handle it?',
    instruction: 'Write a brief response (2-3 paragraphs):'
  }, {
    type: 'single-choice',
    text: 'Your team is experiencing conflict over the technical direction of a project. Team members have different opinions on which technology stack to use.',
    question: 'What leadership approach would be most effective in this situation?',
    instruction: 'Select the most effective approach:',
    options: ['Make the decision yourself to save time and avoid further conflict.', 'Facilitate a structured discussion to understand all perspectives before deciding.', 'Let the most senior team member decide.', 'Postpone the decision until everyone calms down.']
  }, {
    type: 'single-choice',
    text: "You've identified a risk that could significantly impact the project timeline. The risk involves a potential delay in receiving critical components from a vendor.",
    question: 'What should you do first?',
    instruction: 'Choose the most appropriate first action:',
    options: ['Add more resources to the project immediately.', 'Document the risk in the risk register with potential impacts and mitigation strategies.', 'Hide the risk to avoid alarming stakeholders.', 'Wait to see if the risk actually materializes before taking action.']
  }, {
    type: 'text',
    text: 'Effective communication is essential when working with diverse stakeholders who have varying levels of technical expertise.',
    question: 'Explain how you would develop a communication plan for a project with diverse stakeholders who have different levels of technical expertise.',
    instruction: 'Write a brief response:'
  }, {
    type: 'single-choice',
    text: 'Your project is running behind schedule due to unexpected technical challenges. Stakeholders are asking for updates.',
    question: 'Which of the following would be the most appropriate action?',
    instruction: 'Select the best course of action:',
    options: ['Hide the delay from stakeholders until you can catch up.', 'Blame team members who are behind on their tasks.', 'Analyze the critical path, identify opportunities to fast-track, and communicate the situation to stakeholders.', 'Add resources without analyzing the root cause of the delay.']
  }, {
    type: 'multiple-choice',
    text: 'Managing a diverse project team requires understanding different perspectives, work styles, and cultural backgrounds.',
    question: 'When managing a diverse project team, which approaches are most likely to lead to success?',
    instruction: 'Select all that apply:',
    options: ['Treating everyone exactly the same regardless of their background or working style.', 'Recognizing different perspectives and adapting your leadership style accordingly.', 'Focusing exclusively on technical skills and ignoring cultural differences.', 'Creating opportunities for team members to share their unique perspectives.']
  }, {
    type: 'single-choice',
    text: 'A key stakeholder consistently misses scheduled meetings and is slow to respond to important communications. This is affecting project progress.',
    question: 'What should you do?',
    instruction: 'Choose the most appropriate response:',
    options: ["Exclude them from future communications since they don't seem interested.", 'Escalate to their manager immediately.', 'Try to understand the underlying reasons and work with them to find a communication approach that works better.', "Continue with the current approach and hope they'll eventually engage."]
  }, {
    type: 'text',
    text: 'Complex projects often involve multiple interdependencies between tasks, resources, and deliverables.',
    question: 'Describe how you would approach creating a project schedule for a complex project with many interdependencies.',
    instruction: 'Write a brief response:'
  }, {
    type: 'single-choice',
    text: "You need to provide negative feedback to a team member about their work. The team member's deliverables have not met quality standards.",
    question: 'What approach would be most effective?',
    instruction: 'Select the best approach:',
    options: ['Send an email detailing all their mistakes to create a paper trail.', 'Provide specific examples in a private conversation, focusing on behaviors rather than personal attributes.', 'Give the feedback in a team meeting so others can learn from their mistakes.', 'Hint at the problems indirectly to avoid confrontation.']
  }, {
    type: 'multiple-choice',
    text: 'Project closure is an important phase that provides valuable insights for future projects.',
    question: 'When closing a project, which of the following activities are most valuable for organizational learning?',
    instruction: 'Select all that apply:',
    options: ['Conducting a thorough lessons learned session and documenting the findings.', 'Celebrating project successes and acknowledging team contributions.', 'Analyzing what went well and what could be improved for future projects.', 'Creating a knowledge repository that can be accessed by future project teams.']
  }];
  // Get the active questions based on retake mode
  const getActiveQuestions = () => {
    // Always return all questions - we'll handle locking correct ones in the UI
    return questions;
  };
  // Get the original question index when in focused retake mode
  const getOriginalQuestionIndex = currentIndex => {
    // Since we're always showing all questions now, just return the current index
    return currentIndex;
  };
  // Check if a question should be locked (already correct in focused retake mode)
  const isQuestionLocked = questionIndex => {
    if (retakingAssessment && incorrectQuestions.length > 0) {
      // If we're in focused retake mode, lock questions that are NOT in the incorrect list
      return !incorrectQuestions.includes(questionIndex);
    }
    return false;
  };
  // Check if a question needs attention (incorrect in focused retake mode)
  const isQuestionNeedsAttention = questionIndex => {
    if (retakingAssessment && incorrectQuestions.length > 0) {
      return incorrectQuestions.includes(questionIndex);
    }
    return false;
  };
  const activeQuestions = getActiveQuestions();
  const totalQuestions = activeQuestions.length;
  // Safety check: if activeQuestions is empty or currentQuestion is out of bounds, reset
  useEffect(() => {
    if (activeQuestions.length === 0 || currentQuestion >= activeQuestions.length) {
      setCurrentQuestion(0);
      setIsOnSubmissionScreen(false);
    }
  }, [activeQuestions.length, currentQuestion]);
  // Get current question with safety check
  const getCurrentQuestion = () => {
    if (!activeQuestions || activeQuestions.length === 0) {
      return null;
    }
    if (currentQuestion >= activeQuestions.length) {
      return null;
    }
    return activeQuestions[currentQuestion];
  };
  const currentQuestionData = getCurrentQuestion();
  // Calculate how many dots to show
  const MOBILE_DOTS_VISIBLE = 5;
  const DESKTOP_DOTS_VISIBLE = 8; // Show 8 questions at a time on desktop
  // Auto-scroll progress dots to keep current question visible on mobile
  useEffect(() => {
    if (!isOnSubmissionScreen) {
      const halfVisible = Math.floor(MOBILE_DOTS_VISIBLE / 2);
      const newStartIndex = Math.max(0, Math.min(currentQuestion - halfVisible, totalQuestions - MOBILE_DOTS_VISIBLE));
      setProgressDotStartIndex(newStartIndex);
    }
  }, [currentQuestion, isOnSubmissionScreen, totalQuestions]);
  // Auto-scroll progress dots to keep current question visible on desktop
  useEffect(() => {
    if (!isOnSubmissionScreen && totalQuestions > DESKTOP_DOTS_VISIBLE) {
      const halfVisible = Math.floor(DESKTOP_DOTS_VISIBLE / 2);
      const newStartIndex = Math.max(0, Math.min(currentQuestion - halfVisible, totalQuestions - DESKTOP_DOTS_VISIBLE));
      setDesktopDotStartIndex(newStartIndex);
    }
  }, [currentQuestion, isOnSubmissionScreen, totalQuestions]);
  const scrollProgressDots = direction => {
    setProgressDotStartIndex(prev => {
      if (direction === 'left') {
        return Math.max(0, prev - 1);
      } else {
        return Math.min(totalQuestions - MOBILE_DOTS_VISIBLE, prev + 1);
      }
    });
  };
  const scrollDesktopProgressDots = direction => {
    setDesktopDotStartIndex(prev => {
      if (direction === 'left') {
        return Math.max(0, prev - 1);
      } else {
        return Math.min(totalQuestions - DESKTOP_DOTS_VISIBLE, prev + 1);
      }
    });
  };
  // Mock assessment results (would normally come from backend)
  const assessmentResults = [{
    isCorrect: true,
    correctAnswers: [1],
    feedback: 'Excellent choice! Documenting the change request and analyzing its impact is the proper first step in change management.'
  }, {
    isCorrect: false,
    correctAnswers: [1],
    feedback: 'Remember that stakeholder changes need to be evaluated for their impact on project constraints before proceeding.'
  }, {
    isCorrect: true,
    correctAnswers: null,
    feedback: "Good response. You've demonstrated an understanding of stakeholder management and conflict resolution."
  }, {
    isCorrect: true,
    correctAnswers: [1],
    feedback: 'Correct! Facilitating a structured discussion allows all perspectives to be heard and leads to better decisions.'
  }, {
    isCorrect: true,
    correctAnswers: [1],
    feedback: 'Exactly right. Documenting the risk is the first step in proper risk management.'
  }, {
    isCorrect: true,
    correctAnswers: null,
    feedback: 'Great explanation of how to tailor communication for different stakeholders.'
  }, {
    isCorrect: true,
    correctAnswers: [2],
    feedback: 'Correct! Analyzing the critical path and communicating with stakeholders is the most appropriate action.'
  }, {
    isCorrect: true,
    correctAnswers: [1, 3],
    feedback: 'Good choices! Recognizing different perspectives and creating opportunities for sharing are key to managing diverse teams.'
  }, {
    isCorrect: true,
    correctAnswers: [2],
    feedback: 'Excellent! Understanding underlying reasons and finding better communication approaches is the most effective strategy.'
  }, {
    isCorrect: false,
    correctAnswers: null,
    feedback: 'Your approach could be improved by focusing more on critical path analysis and resource allocation.'
  }, {
    isCorrect: true,
    correctAnswers: [1],
    feedback: 'Correct! Private conversations focusing on behaviors rather than personal attributes is the most effective approach.'
  }, {
    isCorrect: true,
    correctAnswers: [0, 1, 2, 3],
    feedback: 'Perfect! All of these activities are valuable for organizational learning when closing a project.'
  }];
  const hasAnswer = questionIndex => {
    // questionIndex here should be the ACTIVE question index
    const question = activeQuestions[questionIndex];
    if (!question) return false; // Add safety check
    // Get the original index to check the answers
    const originalIndex = getOriginalQuestionIndex(questionIndex);
    if (question.type === 'text') {
      return textAnswers[originalIndex] && textAnswers[originalIndex].trim() !== '';
    } else if (question.type === 'multiple-choice' || question.type === 'single-choice') {
      return selectedOptions[originalIndex] && selectedOptions[originalIndex].length > 0;
    }
    return false;
  };
  const isQuestionCompleted = questionIndex => {
    // questionIndex here should be the ACTIVE question index
    // A question is only considered completed if it has both an answer AND a confidence level
    const originalIndex = getOriginalQuestionIndex(questionIndex);
    return hasAnswer(questionIndex) && confidenceLevel[originalIndex];
  };
  // Helper function to check if a question has an answer but no confidence level
  const hasAnswerButNoConfidence = questionIndex => {
    // questionIndex here should be the ACTIVE question index
    const originalIndex = getOriginalQuestionIndex(questionIndex);
    return hasAnswer(questionIndex) && !confidenceLevel[originalIndex];
  };
  // Update the status of the current question before navigating
  const updateCurrentQuestionStatus = () => {
    const originalIndex = getOriginalQuestionIndex(currentQuestion);
    if (isQuestionCompleted(currentQuestion)) {
      // Mark as completed if it's not already
      if (!completedQuestions.includes(originalIndex)) {
        setCompletedQuestions(prev => [...prev, originalIndex]);
        // Remove from incomplete if it was previously incomplete
        setIncompleteQuestions(prev => prev.filter(q => q !== originalIndex));
      }
    } else {
      // Mark as incomplete if it's not already and we're moving away from it
      if (!incompleteQuestions.includes(originalIndex)) {
        setIncompleteQuestions(prev => [...prev, originalIndex]);
        // Remove from completed if it was previously completed
        setCompletedQuestions(prev => prev.filter(q => q !== originalIndex));
      }
    }
  };
  // Mark questions as incomplete when jumping ahead
  const markSkippedQuestionsAsIncomplete = targetIndex => {
    if (targetIndex > currentQuestion) {
      // Find questions that will be jumped over
      const newIncomplete = [];
      for (let i = currentQuestion + 1; i < targetIndex; i++) {
        const originalIndex = getOriginalQuestionIndex(i);
        // Only mark as incomplete if not already completed
        if (!completedQuestions.includes(originalIndex) && !incompleteQuestions.includes(originalIndex)) {
          newIncomplete.push(originalIndex);
        }
      }
      if (newIncomplete.length > 0) {
        setIncompleteQuestions(prev => [...prev, ...newIncomplete]);
      }
    }
  };
  const nextQuestion = () => {
    // Update status of current question
    updateCurrentQuestionStatus();
    if (currentQuestion === totalQuestions - 1) {
      // If on the last question, go to submission screen
      setIsOnSubmissionScreen(true);
    } else {
      // Otherwise go to next question
      setCurrentQuestion(prev => Math.min(prev + 1, totalQuestions - 1));
    }
  };
  const prevQuestion = () => {
    // Update status of current question
    updateCurrentQuestionStatus();
    if (isOnSubmissionScreen) {
      // If on submission screen, go back to last question
      setIsOnSubmissionScreen(false);
    } else {
      // Otherwise go to previous question
      setCurrentQuestion(prev => Math.max(prev - 1, 0));
    }
  };
  const toggleOption = (questionIndex, optionIndex) => {
    setSelectedOptions(prev => {
      const currentSelected = prev[questionIndex] || [];
      // Use the active questions array to get the question type
      const activeIndex = retakingAssessment && incorrectQuestions.length > 0 ? incorrectQuestions.indexOf(questionIndex) : questionIndex;
      const question = activeQuestions[activeIndex];
      if (!question) return prev; // Safety check
      if (question.type === 'single-choice') {
        // For single choice, replace the selection
        return {
          ...prev,
          [questionIndex]: [optionIndex]
        };
      } else {
        // For multiple choice, toggle the selection
        if (currentSelected.includes(optionIndex)) {
          return {
            ...prev,
            [questionIndex]: currentSelected.filter(i => i !== optionIndex)
          };
        } else {
          return {
            ...prev,
            [questionIndex]: [...currentSelected, optionIndex]
          };
        }
      }
    });
  };
  const handleTextChange = (questionIndex, value) => {
    setTextAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
  };
  const bind = useDrag(({
    swipe: [swipeX]
  }) => {
    if (swipeX < 0) nextQuestion();
    if (swipeX > 0) prevQuestion();
  });
  const getQuestionStatus = index => {
    // In focused retake mode, show different statuses
    if (retakingAssessment && incorrectQuestions.length > 0) {
      if (incorrectQuestions.includes(index)) {
        // Check if this question has been completed (answered + confidence level)
        // If completed, show as completed-retake (gray with NO indicator)
        if (isQuestionCompleted(index)) {
          return 'completed-retake';
        }
        // This question needs attention - don't mark as completed even if answered
        return 'needs-attention';
      } else {
        // This question was already correct
        return 'locked-correct';
      }
    }
    if (completedQuestions.includes(index)) return 'completed';
    if (incompleteQuestions.includes(index)) return 'incomplete';
    return 'pending';
  };
  const handleSubmit = () => {
    if (incompleteQuestions.length > 0) {
      setShowSubmitModal(true);
    } else {
      // Submit the assessment
      console.log('Assessment submitted successfully');
      setIsSubmitted(true);
      // Here you would typically send the data to your backend
    }
  };
  const confirmSubmit = () => {
    // Submit the assessment even with unanswered questions
    console.log('Assessment submitted with unanswered questions');
    setShowSubmitModal(false);
    setIsSubmitted(true);
    // Here you would typically send the data to your backend
  };
  const goToQuestion = questionIndex => {
    // Mark questions as incomplete if jumping ahead
    markSkippedQuestionsAsIncomplete(questionIndex);
    // Update status of current question before navigating
    updateCurrentQuestionStatus();
    setCurrentQuestion(questionIndex);
    setIsOnSubmissionScreen(false);
  };
  const goToSubmission = () => {
    // Mark any remaining questions as incomplete
    markSkippedQuestionsAsIncomplete(totalQuestions);
    // Update status of current question before navigating
    updateCurrentQuestionStatus();
    setIsOnSubmissionScreen(true);
  };
  const setConfidence = level => {
    setConfidenceLevel(prev => ({
      ...prev,
      [currentQuestion]: level
    }));
  };
  const handleBackToAssessment = (focusOnMissed = null) => {
    if (focusOnMissed && focusOnMissed.length > 0) {
      // If we're focusing on missed questions, mark them as incorrect
      console.log('Retaking assessment with focus on missed questions:', focusOnMissed);
      setIncorrectQuestions(focusOnMissed);
      setRetakingAssessment(true);
      // Reset current question to the first one
      setCurrentQuestion(0);
      // Reset completed and incomplete questions
      setCompletedQuestions([]);
      setIncompleteQuestions([]);
      // Reset submission screen state
      setIsOnSubmissionScreen(false);
    } else if (focusOnMissed === null) {
      // If we're retaking the entire assessment, just mark that we're retaking
      setRetakingAssessment(true);
      // Reset current question to the first one
      setCurrentQuestion(0);
      // Reset completed and incomplete questions
      setCompletedQuestions([]);
      setIncompleteQuestions([]);
      // Reset incorrect questions array if retaking the entire assessment
      setIncorrectQuestions([]);
      // Reset submission screen state
      setIsOnSubmissionScreen(false);
      // Reset all user answers when retaking the entire assessment
      setSelectedOptions({});
      setTextAnswers({});
      setConfidenceLevel({});
    }
    setIsSubmitted(false);
  };
  // If the assessment is submitted, show the feedback page
  if (isSubmitted) {
    return <FeedbackPage assessmentResults={assessmentResults} questions={questions} userAnswers={selectedOptions} confidenceLevels={confidenceLevel} onBackToAssessment={handleBackToAssessment} data-id="element-7" />;
  }
  // Safety check: if no questions available, show error state
  if (!activeQuestions || activeQuestions.length === 0) {
    return <div className="flex-1 p-4 md:p-8 relative" data-id="element-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 max-w-3xl mx-auto text-center" data-id="element-9">
          <AlertTriangleIcon size={48} className="text-yellow-500 mx-auto mb-4" data-id="element-10" />
          <h2 className="text-xl font-semibold mb-2" data-id="element-11">No Questions Available</h2>
          <p className="text-gray-600 mb-4" data-id="element-12">
            There are no questions to display. Please try again.
          </p>
          <button onClick={() => {
          setRetakingAssessment(false);
          setIncorrectQuestions([]);
          setCurrentQuestion(0);
        }} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-13">
            Reset Assessment
          </button>
        </div>
      </div>;
  }
  return <Tooltip.Provider delayDuration={300} data-id="element-14">
      <div className="flex-1 p-2 sm:p-3 md:p-4 lg:p-8 relative min-h-screen pb-20 md:pb-8" data-id="element-15">
        {/* Review Mode Badge - Above Progress Dots */}
        {retakingAssessment && incorrectQuestions.length > 0 && <div className="mb-3 flex justify-center" data-id="element-16">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-full shadow-sm" data-id="element-17">
              <RefreshCcw size={14} className="animate-spin" style={{
            animationDuration: '3s'
          }} data-id="element-18" />
              <span className="text-sm font-medium" data-id="element-19">Review Mode</span>
            </div>
          </div>}
        {/* Progress Dots - Mobile Carousel */}
        <div className="mb-2 sm:mb-4 md:mb-6 lg:mb-8" data-id="element-20">
          {/* Mobile View - Carousel */}
          <div className="sm:hidden" data-id="element-21">
            <div className="flex items-center justify-center gap-1 py-2.5" data-id="element-22">
              {/* Left Arrow */}
              <button onClick={() => scrollProgressDots('left')} disabled={progressDotStartIndex === 0} className="w-7 h-7 flex items-center justify-center text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0" data-id="element-23">
                <ChevronLeft size={16} data-id="element-24" />
              </button>
              {/* Progress Dots Container */}
              <div className="flex items-center gap-1 py-1" data-id="element-25">
                {Array.from({
                length: totalQuestions
              }).slice(progressDotStartIndex, progressDotStartIndex + MOBILE_DOTS_VISIBLE).map((_, index) => {
                const actualIndex = progressDotStartIndex + index;
                const originalIndex = getOriginalQuestionIndex(actualIndex);
                const status = getQuestionStatus(originalIndex);
                return <button key={actualIndex} onClick={() => goToQuestion(actualIndex)} className={`w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center transition-all text-[10px] font-medium relative
                        ${actualIndex === currentQuestion && !isOnSubmissionScreen ? 'bg-[#00638C] text-white ring-2 ring-[#00638C]/30 scale-110' : status === 'completed' ? 'bg-green-500 text-white' : status === 'locked-correct' ? 'bg-gray-300 text-gray-600' : status === 'completed-retake' ? 'bg-gray-300 text-gray-600' : status === 'needs-attention' ? 'bg-gray-400 text-white' : status === 'incorrect' ? 'bg-gray-400 text-white' : status === 'incomplete' ? 'bg-yellow-400 text-white' : 'bg-gray-300 text-gray-700'}`} data-id="element-26">
                        {actualIndex + 1}
                        {status === 'completed' && <span className="absolute -top-0.5 -right-0.5 bg-green-600 rounded-full w-2.5 h-2.5 flex items-center justify-center border border-white" data-id="element-27">
                            <CheckIcon size={6} className="text-white" strokeWidth={3} data-id="element-28" />
                          </span>}
                        {status === 'locked-correct' && <span className="absolute -top-0.5 -right-0.5 bg-green-600 rounded-full w-2.5 h-2.5 flex items-center justify-center border border-white" data-id="element-29">
                            <CheckIcon size={6} className="text-white" strokeWidth={3} data-id="element-30" />
                          </span>}
                        {(status === 'needs-attention' || status === 'incorrect') && <span className="absolute -top-0.5 -right-0.5 bg-yellow-500 rounded-full w-2.5 h-2.5 flex items-center justify-center border border-white" data-id="element-31">
                            <span className="text-white font-bold text-[6px]" data-id="element-32">
                              !
                            </span>
                          </span>}
                        {status === 'incomplete' && <span className="absolute -top-0.5 -right-0.5 bg-yellow-600 rounded-full w-2.5 h-2.5 flex items-center justify-center border border-white" data-id="element-33">
                            <span className="text-white font-bold text-[6px]" data-id="element-34">
                              !
                            </span>
                          </span>}
                      </button>;
              })}
              </div>
              {/* Right Arrow */}
              <button onClick={() => scrollProgressDots('right')} disabled={progressDotStartIndex >= totalQuestions - MOBILE_DOTS_VISIBLE} className="w-7 h-7 flex items-center justify-center text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0" data-id="element-35">
                <ChevronRight size={16} data-id="element-36" />
              </button>
              {/* Review Button */}
              <button onClick={goToSubmission} className={`flex-shrink-0 flex items-center justify-center rounded-md px-2 py-0.5 text-[10px] font-medium transition-all ml-1
                  ${isOnSubmissionScreen ? 'bg-[#00638C] text-white ring-2 ring-[#00638C]/30' : 'bg-gray-200 text-gray-700 border border-gray-300'}`} data-id="element-37">
                Review
              </button>
            </div>
            {/* Progress Indicator */}
            <div className="text-center mt-1" data-id="element-38"></div>
          </div>
          {/* Desktop View - Carousel */}
          <div className="hidden sm:block" data-id="element-39">
            <div className="flex items-center justify-center gap-2 md:gap-3 py-3 px-1" data-id="element-40">
              {/* Left Arrow - Only show if we can scroll left */}
              {totalQuestions > DESKTOP_DOTS_VISIBLE && <button onClick={() => scrollDesktopProgressDots('left')} disabled={desktopDotStartIndex === 0} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent flex-shrink-0" data-id="element-41">
                  <ChevronLeft size={20} data-id="element-42" />
                </button>}
              {/* Progress Dots Container */}
              <div className="flex items-center gap-2 md:gap-3" data-id="element-43">
                {Array.from({
                length: Math.min(totalQuestions, DESKTOP_DOTS_VISIBLE)
              }).map((_, index) => {
                const actualIndex = desktopDotStartIndex + index;
                const originalIndex = getOriginalQuestionIndex(actualIndex);
                const status = getQuestionStatus(originalIndex);
                return <Tooltip.Root key={actualIndex} data-id="element-44">
                      <Tooltip.Trigger asChild data-id="element-45">
                        <button onClick={() => goToQuestion(actualIndex)} className={`w-8 h-8 md:w-9 md:h-9 flex-shrink-0 rounded-full flex items-center justify-center transition-all text-sm font-medium relative
                            ${actualIndex === currentQuestion && !isOnSubmissionScreen ? 'bg-[#00638C] text-white ring-2 ring-[#00638C]/30 scale-110' : status === 'completed' ? 'bg-green-500 text-white' : status === 'locked-correct' ? 'bg-gray-300 text-gray-600' : status === 'completed-retake' ? 'bg-gray-300 text-gray-600' : status === 'needs-attention' ? 'bg-gray-400 text-white' : status === 'incorrect' ? 'bg-gray-400 text-white' : status === 'incomplete' ? 'bg-yellow-400 text-white' : 'bg-gray-300 text-gray-700 hover:bg-gray-400'}`} data-id="element-46">
                          {actualIndex + 1}
                          {status === 'completed' && <span className="absolute -top-0.5 -right-0.5 bg-green-600 rounded-full w-3.5 h-3.5 flex items-center justify-center border-2 border-white shadow-sm" data-id="element-47">
                              <CheckIcon size={10} className="text-white" strokeWidth={3} data-id="element-48" />
                            </span>}
                          {status === 'locked-correct' && <span className="absolute -top-0.5 -right-0.5 bg-green-600 rounded-full w-3.5 h-3.5 flex items-center justify-center border-2 border-white shadow-sm" data-id="element-49">
                              <CheckIcon size={10} className="text-white" strokeWidth={3} data-id="element-50" />
                            </span>}
                          {(status === 'needs-attention' || status === 'incorrect') && <span className="absolute -top-0.5 -right-0.5 bg-yellow-500 rounded-full w-3.5 h-3.5 flex items-center justify-center border-2 border-white shadow-sm" data-id="element-51">
                              <span className="text-white font-bold text-[8px]" data-id="element-52">
                                !
                              </span>
                            </span>}
                          {status === 'incomplete' && <span className="absolute -top-0.5 -right-0.5 bg-yellow-600 rounded-full w-3.5 h-3.5 flex items-center justify-center border-2 border-white shadow-sm" data-id="element-53">
                              <span className="text-white font-bold text-[8px]" data-id="element-54">
                                !
                              </span>
                            </span>}
                        </button>
                      </Tooltip.Trigger>
                      <Tooltip.Portal data-id="element-55">
                        <Tooltip.Content className="bg-gray-900 text-white px-2 py-1 rounded-md text-xs" sideOffset={5} side="top" align="center" data-id="element-56">
                          Question {originalIndex + 1}:{' '}
                          {status === 'locked-correct' ? 'Already Correct (Locked)' : status === 'completed-retake' ? 'Completed' : status === 'needs-attention' ? 'Needs Attention' : status === 'incorrect' ? 'Needs Attention' : status.charAt(0).toUpperCase() + status.slice(1)}
                          <Tooltip.Arrow className="fill-gray-900" data-id="element-57" />
                        </Tooltip.Content>
                      </Tooltip.Portal>
                    </Tooltip.Root>;
              })}
              </div>
              {/* Right Arrow - Only show if we can scroll right */}
              {totalQuestions > DESKTOP_DOTS_VISIBLE && <button onClick={() => scrollDesktopProgressDots('right')} disabled={desktopDotStartIndex >= totalQuestions - DESKTOP_DOTS_VISIBLE} className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent flex-shrink-0" data-id="element-58">
                  <ChevronRight size={20} data-id="element-59" />
                </button>}
              {/* Submission indicator */}
              <Tooltip.Root data-id="element-60">
                <Tooltip.Trigger asChild data-id="element-61">
                  <button onClick={goToSubmission} className={`flex-shrink-0 flex items-center justify-center rounded-md px-3 md:px-4 py-1 text-sm font-medium transition-all ml-2
                      ${isOnSubmissionScreen ? 'bg-[#00638C] text-white ring-2 ring-[#00638C]/30 scale-105' : 'bg-gray-200 text-gray-700 border border-gray-300 hover:bg-gray-300'}`} data-id="element-62">
                    Review
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal data-id="element-63">
                  <Tooltip.Content className="bg-gray-900 text-white px-2 py-1 rounded-md text-xs" sideOffset={5} side="top" align="center" data-id="element-64">
                    Review and submit your assessment
                    <Tooltip.Arrow className="fill-gray-900" data-id="element-65" />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </div>
            {/* Progress Indicator - Show current position */}
            {totalQuestions > DESKTOP_DOTS_VISIBLE && <div className="text-center mt-1" data-id="element-66">
                <span className="text-xs text-gray-500" data-id="element-67">
                  Showing {desktopDotStartIndex + 1}-
                  {Math.min(desktopDotStartIndex + DESKTOP_DOTS_VISIBLE, totalQuestions)}{' '}
                  of {totalQuestions} questions
                </span>
              </div>}
          </div>
        </div>
        {/* Question Card or Submission Screen */}
        <AnimatePresence mode="wait" data-id="element-68">
          {isOnSubmissionScreen ? <motion.div key="submission-screen" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 md:p-6 lg:p-8 max-w-[1000px] mx-auto" data-id="element-69">
              <div className="flex items-start gap-2 sm:gap-3 md:gap-4 mb-3 sm:mb-4 md:mb-6" data-id="element-70">
                <div className="w-8 h-8 sm:w-11 sm:h-11 md:w-12 md:h-12 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-71">
                  <CheckSquareIcon size={16} className="sm:w-[22px] sm:h-[22px] md:w-6 md:h-6 text-gray-600" data-id="element-72" />
                </div>
                <div className="min-w-0 flex-1" data-id="element-73">
                  <h1 className="text-base sm:text-lg md:text-xl font-semibold mb-0.5 sm:mb-1" data-id="element-74">
                    Ready to Submit?
                  </h1>
                  <p className="text-gray-600 text-sm sm:text-base" data-id="element-75">
                    Please review your answers before submitting
                  </p>
                </div>
              </div>
              {/* Assessment Summary */}
              <div className="mb-4 sm:mb-5 md:mb-8" data-id="element-76">
                <div className="p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200 mb-3 sm:mb-4 md:mb-6" data-id="element-77">
                  <h2 className="font-semibold mb-2 sm:mb-2.5 md:mb-3 text-base" data-id="element-78">
                    Assessment Summary
                  </h2>
                  <div className="space-y-1.5 sm:space-y-2" data-id="element-79">
                    <div className="flex items-center justify-between" data-id="element-80">
                      <span className="text-sm text-gray-600" data-id="element-81">
                        Total Questions:
                      </span>
                      <span className="font-medium text-base" data-id="element-82">
                        {totalQuestions}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-83">
                      <span className="text-sm text-gray-600" data-id="element-84">Completed:</span>
                      <span className="font-medium text-[#00638C] text-base" data-id="element-85">
                        {completedQuestions.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-86">
                      <span className="text-sm text-gray-600" data-id="element-87">Incomplete:</span>
                      <span className="font-medium text-yellow-600 text-base" data-id="element-88">
                        {incompleteQuestions.length}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Incomplete Questions */}
                {incompleteQuestions.length > 0 && <div className="mb-3 sm:mb-4 md:mb-6" data-id="element-89">
                    <div className="flex items-center mb-2 sm:mb-2.5 md:mb-3" data-id="element-90">
                      <AlertTriangleIcon size={16} className="sm:w-[18px] sm:h-[18px] text-yellow-500 mr-1.5 sm:mr-2 flex-shrink-0" data-id="element-91" />
                      <h3 className="font-semibold text-base" data-id="element-92">
                        You have incomplete questions
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 sm:mb-2.5 md:mb-4" data-id="element-93">
                      You must complete all questions before submitting:
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-94">
                      {incompleteQuestions.map(questionIndex => <button key={questionIndex} onClick={() => goToQuestion(questionIndex)} className="py-2 px-2 sm:px-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm hover:bg-yellow-100 transition-colors" data-id="element-95">
                          Question {questionIndex + 1}
                        </button>)}
                    </div>
                  </div>}
              </div>
              {/* Submit Button */}
              <button onClick={handleSubmit} disabled={incompleteQuestions.length > 0} className="w-full py-2.5 sm:py-3 bg-[#00638C] text-white rounded-lg font-medium hover:bg-[#004d6d] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed disabled:hover:bg-gray-300 text-base" data-id="element-96">
                {incompleteQuestions.length > 0 ? `Complete ${incompleteQuestions.length} Question${incompleteQuestions.length !== 1 ? 's' : ''} to Submit` : 'Submit Assessment'}
              </button>
            </motion.div> : currentQuestionData ? <>
              {/* Locked Question Banner - Outside question card */}
              {isQuestionLocked(currentQuestion) && <div className="mb-4 p-3 sm:p-4 bg-[#00638C]/10 border-l-4 border-[#00638C] rounded max-w-[1000px] mx-auto" data-id="element-97">
                  <div className="flex items-start gap-2 sm:gap-3" data-id="element-98">
                    <CheckIcon size={18} className="sm:w-5 sm:h-5 text-[#00638C] flex-shrink-0 mt-0.5" data-id="element-99" />
                    <div data-id="element-100">
                      <p className="text-sm font-semibold text-gray-900 mb-1" data-id="element-101">
                        Already Answered Correctly
                      </p>
                      <p className="text-sm text-gray-900" data-id="element-102">
                        You got this question right on your previous attempt.
                        Your answer is locked and you can review it below.
                      </p>
                    </div>
                  </div>
                </div>}
              {/* Needs Attention Banner - Outside question card */}
              {isQuestionNeedsAttention(currentQuestion) && <div className="mb-4 p-3 sm:p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded max-w-[1000px] mx-auto" data-id="element-103">
                  <div className="flex items-start gap-2 sm:gap-3" data-id="element-104">
                    <AlertTriangleIcon size={18} className="sm:w-5 sm:h-5 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-105" />
                    <div data-id="element-106">
                      <p className="text-sm font-semibold text-yellow-900 mb-1" data-id="element-107">
                        Needs Your Attention
                      </p>
                      <p className="text-sm text-yellow-800" data-id="element-108">
                        This question was answered incorrectly. Please review
                        and provide a new answer.
                      </p>
                    </div>
                  </div>
                </div>}
              <motion.div key={currentQuestion} initial={{
            opacity: 0,
            x: 100
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -100
          }} className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 p-2.5 sm:p-4 md:p-6 lg:p-8 max-w-[1000px] mx-auto" {...bind()} data-id="element-109">
                {/* Scenario Accordion */}
                <div className="mb-3 sm:mb-4 md:mb-5" data-id="element-110">
                  <button onClick={() => setIsScenarioExpanded(!isScenarioExpanded)} className="w-full flex items-center justify-between p-2.5 sm:p-3 bg-gray-50 hover:bg-gray-100 transition-colors rounded-lg border border-gray-200" data-id="element-111">
                    <span className="text-sm font-medium text-gray-700" data-id="element-112">
                      {isScenarioExpanded ? 'Hide' : 'Show'} scenario:
                      Stakeholder Meeting Observation
                    </span>
                    <ChevronDown size={16} className={`sm:w-5 sm:h-5 text-gray-600 transition-transform ${isScenarioExpanded ? 'rotate-180' : ''}`} data-id="element-113" />
                  </button>
                  <AnimatePresence data-id="element-114">
                    {isScenarioExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.2
                }} className="overflow-hidden" data-id="element-115">
                        <div className="p-2.5 sm:p-3 md:p-4 mt-2 bg-gray-50 rounded-lg border border-gray-200" data-id="element-116">
                          <p className="text-gray-800 text-base leading-relaxed" data-id="element-117">
                            {currentQuestionData.text}
                          </p>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
                {/* Question Header */}
                <div className="mb-3 sm:mb-4 md:mb-5" data-id="element-118">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3" data-id="element-119">
                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900" data-id="element-120">
                      Question {currentQuestion + 1} of {totalQuestions}
                    </h2>
                  </div>
                  {/* Question Text - Now showing the separate question field */}
                  <p className="text-gray-800 mb-3 sm:mb-4 text-base leading-relaxed font-medium" data-id="element-121">
                    {currentQuestionData.question}
                  </p>
                  {/* Instruction */}
                  <div className="p-2.5 sm:p-3 md:p-4 bg-[#00638C]/10 border-l-4 border-[#00638C] rounded" data-id="element-122">
                    <p className="text-gray-900 font-semibold text-base leading-relaxed" data-id="element-123">
                      {currentQuestionData.instruction}
                    </p>
                  </div>
                </div>
                <div className="space-y-2 sm:space-y-3.5 md:space-y-5 lg:space-y-6" data-id="element-124">
                  {/* Question Content */}
                  {currentQuestionData.type === 'text' ? <div className="relative" data-id="element-125">
                      <textarea value={textAnswers[getOriginalQuestionIndex(currentQuestion)] || ''} onChange={e => handleTextChange(getOriginalQuestionIndex(currentQuestion), e.target.value)} placeholder="Type your answer here..." disabled={isQuestionLocked(currentQuestion)} className={`w-full min-h-[100px] sm:min-h-[140px] md:min-h-[150px] p-2 sm:p-3 md:p-4 rounded-lg border border-gray-200 focus:border-[#00638C] focus:ring-1 focus:ring-[#00638C] outline-none resize-y text-base ${isQuestionLocked(currentQuestion) ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`} data-id="element-126" />
                      {isQuestionLocked(currentQuestion) && <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium" data-id="element-127">
                          Locked
                        </div>}
                    </div> : <div className="space-y-1.5 sm:space-y-2.5 md:space-y-4" data-id="element-128">
                      {currentQuestionData.options?.map((option, i) => {
                  const originalIndex = getOriginalQuestionIndex(currentQuestion);
                  const isSelected = selectedOptions[originalIndex]?.includes(i) || false;
                  const inputType = currentQuestionData.type === 'single-choice' ? 'radio' : 'checkbox';
                  const locked = isQuestionLocked(currentQuestion);
                  return <label key={i} className={`flex items-start p-2 sm:p-3 md:p-4 rounded-lg border ${isSelected ? 'border-[#00638C] bg-[#00638C]/5' : 'border-gray-200'} ${locked ? 'bg-gray-100 cursor-not-allowed opacity-75' : 'hover:bg-gray-50 cursor-pointer'} transition-colors relative`} data-id="element-129">
                            <input type={inputType} checked={isSelected} onChange={() => !locked && toggleOption(originalIndex, i)} disabled={locked} className="mr-1.5 sm:mr-2.5 md:mr-3 h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#00638C] rounded flex-shrink-0 mt-0.5 disabled:cursor-not-allowed" data-id="element-130" />
                            <span className="text-base leading-relaxed" data-id="element-131">
                              {option}
                            </span>
                            {locked && isSelected && <span className="ml-auto text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded" data-id="element-132">
                                Your Previous Answer
                              </span>}
                          </label>;
                })}
                    </div>}
                  {/* Confidence Selection */}
                  <div className="space-y-1.5 sm:space-y-2.5 md:space-y-4" data-id="element-133">
                    <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2" data-id="element-134">
                      <h3 className="text-gray-700 font-medium text-base" data-id="element-135">
                        How confident are you in your answer?
                      </h3>
                      <Tooltip.Root data-id="element-136">
                        <Tooltip.Trigger asChild data-id="element-137">
                          <button className="p-0.5 sm:p-1 hover:bg-gray-200 rounded-full transition-colors" data-id="element-138">
                            <InfoIcon size={12} className="sm:w-4 sm:h-4 text-gray-500" data-id="element-139" />
                          </button>
                        </Tooltip.Trigger>
                        <Tooltip.Portal data-id="element-140">
                          <Tooltip.Content className="bg-gray-900 text-white px-3 sm:px-4 py-2 rounded-lg text-sm max-w-[280px] sm:max-w-xs z-50" sideOffset={5} side="top" align="center" data-id="element-141">
                            You'll be asked to indicate how confident you are in
                            each answer you give to help demonstrate your
                            mastery of your new skills.
                            <Tooltip.Arrow className="fill-gray-900" data-id="element-142" />
                          </Tooltip.Content>
                        </Tooltip.Portal>
                      </Tooltip.Root>
                    </div>
                    {hasAnswerButNoConfidence(getOriginalQuestionIndex(currentQuestion)) && !isQuestionLocked(currentQuestion) && <div className="p-2 sm:p-2.5 md:p-3 bg-yellow-50 border-l-4 border-yellow-500 rounded flex items-start gap-1.5 sm:gap-2" data-id="element-143">
                          <AlertTriangleIcon size={12} className="sm:w-4 sm:h-4 text-yellow-500 mt-0.5 flex-shrink-0" data-id="element-144" />
                          <div data-id="element-145">
                            <p className="text-xs sm:text-sm font-medium text-yellow-800" data-id="element-146">
                              Confidence selection required
                            </p>
                            <p className="text-xs sm:text-sm text-yellow-700" data-id="element-147">
                              You've provided an answer, but you need to select
                              your confidence level to complete this question.
                            </p>
                          </div>
                        </div>}
                    <div className="flex flex-col md:flex-row gap-1.5 sm:gap-2.5 md:gap-4" data-id="element-148">
                      <button onClick={() => !isQuestionLocked(currentQuestion) && setConfidence('unsure')} disabled={isQuestionLocked(currentQuestion)} className={`w-full p-2 sm:p-3 md:p-4 rounded-lg border transition-colors relative text-base text-left ${confidenceLevel[getOriginalQuestionIndex(currentQuestion)] === 'unsure' ? isQuestionLocked(currentQuestion) ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed opacity-75' : 'bg-[#00638C]/5 border-[#00638C] text-[#00638C]' : hasAnswerButNoConfidence(getOriginalQuestionIndex(currentQuestion)) && !isQuestionLocked(currentQuestion) ? 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100' : isQuestionLocked(currentQuestion) ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed opacity-50' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`} data-id="element-149">
                        I'm unsure about my answer
                        {confidenceLevel[getOriginalQuestionIndex(currentQuestion)] === 'unsure' && <span className="absolute top-1.5 sm:top-2.5 md:top-3 right-1.5 sm:right-2.5 md:right-3" data-id="element-150">
                            <CheckIcon size={14} className={`sm:w-[18px] sm:h-[18px] ${isQuestionLocked(currentQuestion) ? 'text-gray-600' : 'text-[#00638C]'}`} data-id="element-151" />
                          </span>}
                      </button>
                      <button onClick={() => !isQuestionLocked(currentQuestion) && setConfidence('sure')} disabled={isQuestionLocked(currentQuestion)} className={`w-full p-2 sm:p-3 md:p-4 rounded-lg border transition-colors relative text-base text-left ${confidenceLevel[getOriginalQuestionIndex(currentQuestion)] === 'sure' ? isQuestionLocked(currentQuestion) ? 'bg-gray-100 border-gray-300 text-gray-600 cursor-not-allowed opacity-75' : 'bg-[#00638C]/5 border-[#00638C] text-[#00638C]' : hasAnswerButNoConfidence(getOriginalQuestionIndex(currentQuestion)) && !isQuestionLocked(currentQuestion) ? 'bg-yellow-50 border-yellow-300 hover:bg-yellow-100' : isQuestionLocked(currentQuestion) ? 'bg-gray-50 border-gray-200 text-gray-500 cursor-not-allowed opacity-50' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`} data-id="element-152">
                        I'm sure about my answer
                        {confidenceLevel[getOriginalQuestionIndex(currentQuestion)] === 'sure' && <span className="absolute top-1.5 sm:top-2.5 md:top-3 right-1.5 sm:right-2.5 md:right-3" data-id="element-153">
                            <CheckIcon size={14} className={`sm:w-[18px] sm:h-[18px] ${isQuestionLocked(currentQuestion) ? 'text-gray-600' : 'text-[#00638C]'}`} data-id="element-154" />
                          </span>}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </> : null}
        </AnimatePresence>
        {/* Confirmation Modal */}
        <AnimatePresence data-id="element-155">
          {showSubmitModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" data-id="element-156">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="bg-white rounded-xl p-4 sm:p-6 max-w-md w-full shadow-2xl" data-id="element-157">
                <div className="flex items-center gap-2.5 sm:gap-3 mb-3 sm:mb-4" data-id="element-158">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-159">
                    <AlertTriangleIcon size={18} className="sm:w-5 sm:h-5 text-yellow-600" data-id="element-160" />
                  </div>
                  <h2 className="text-base sm:text-lg font-semibold" data-id="element-161">
                    Incomplete Questions
                  </h2>
                </div>
                <p className="text-gray-600 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base" data-id="element-162">
                  You have {incompleteQuestions.length} incomplete questions.
                  Are you sure you want to submit your assessment? You won't be
                  able to change your answers after submission.
                </p>
                <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 sm:justify-end" data-id="element-163">
                  <button onClick={() => setShowSubmitModal(false)} className="py-2.5 px-4 border border-gray-200 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors order-2 sm:order-1 text-sm sm:text-base" data-id="element-164">
                    Go Back
                  </button>
                  <button onClick={confirmSubmit} className="py-2.5 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors order-1 sm:order-2 text-sm sm:text-base" data-id="element-165">
                    Submit Anyway
                  </button>
                </div>
              </motion.div>
            </div>}
        </AnimatePresence>
        {/* Chat Bot - Desktop Only */}
        <AnimatePresence data-id="element-166">
          {isChatOpen && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: 20
        }} className="hidden md:flex fixed bottom-24 right-6 w-80 h-96 bg-white rounded-xl shadow-xl overflow-hidden flex-col" data-id="element-167">
              <div className="p-4 bg-red-600 text-white flex justify-between items-center" data-id="element-168">
                <h3 className="font-semibold" data-id="element-169">AI Assistant</h3>
                <button onClick={() => setIsChatOpen(false)} className="text-white hover:text-red-100" data-id="element-170">
                  <XIcon size={20} data-id="element-171" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-172">
                {messages.map((message, index) => <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-173">
                    <div className={`max-w-[80%] p-3 rounded-lg ${message.type === 'user' ? 'bg-red-600 text-white' : 'bg-gray-100'}`} data-id="element-174">
                      {message.content}
                    </div>
                  </div>)}
              </div>
              <div className="p-4 border-t" data-id="element-175">
                <div className="flex gap-2" data-id="element-176">
                  <input type="text" placeholder="Ask a question..." className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-red-600" onKeyDown={e => {
                if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                  setMessages([...messages, {
                    type: 'user',
                    content: e.currentTarget.value
                  }]);
                  e.currentTarget.value = '';
                }
              }} data-id="element-177" />
                  <button className="p-2 text-red-600 hover:text-red-700" data-id="element-178">
                    <Send size={20} data-id="element-179" />
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
        {/* Desktop Chat FAB */}
        <button onClick={() => setIsChatOpen(!isChatOpen)} className="hidden md:flex fixed bottom-6 right-6 w-14 h-14 bg-red-600 rounded-full shadow-lg items-center justify-center text-white hover:bg-red-700 transition-colors" data-id="element-180">
          <ListIcon data-id="element-181" />
        </button>
        {/* Navigation Buttons - Desktop Only */}
        <div className={`hidden md:flex ${isOnSubmissionScreen ? 'justify-start' : 'justify-between'} mt-8 max-w-[1000px] mx-auto gap-2`} data-id="element-182">
          <button onClick={prevQuestion} disabled={currentQuestion === 0 && !isOnSubmissionScreen} className="flex items-center px-4 py-2 rounded-lg border border-gray-200 disabled:opacity-50 text-base" data-id="element-183">
            <ChevronLeft size={18} className="mr-1" data-id="element-184" />
            Previous
          </button>
          {!isOnSubmissionScreen && <button onClick={nextQuestion} className="flex items-center px-4 py-2 rounded-lg border border-gray-200 text-base" data-id="element-185">
              {currentQuestion === totalQuestions - 1 ? 'Go to Review' : 'Next'}
              <ChevronRight size={18} className="ml-1" data-id="element-186" />
            </button>}
        </div>
        {/* Mobile Navigation Buttons - Fixed at bottom above bottom nav */}
        <div className="md:hidden fixed bottom-[72px] left-0 right-0 bg-white border-t border-gray-200 p-2.5 sm:p-3 z-20" data-id="element-187">
          <div className={`flex ${isOnSubmissionScreen ? 'justify-start' : 'justify-between'} gap-2`} data-id="element-188">
            <button onClick={prevQuestion} disabled={currentQuestion === 0 && !isOnSubmissionScreen} className="flex items-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 disabled:opacity-50 text-sm font-medium" data-id="element-189">
              <ChevronLeft size={16} className="mr-1" data-id="element-190" />
              <span data-id="element-191">Prev</span>
            </button>
            {!isOnSubmissionScreen && <button onClick={nextQuestion} className="flex items-center px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg border border-gray-200 text-sm font-medium" data-id="element-192">
                <span data-id="element-193">
                  {currentQuestion === totalQuestions - 1 ? 'Review' : 'Next'}
                </span>
                <ChevronRight size={16} className="ml-1" data-id="element-194" />
              </button>}
          </div>
        </div>
      </div>
    </Tooltip.Provider>;
}